import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
} from '@mui/material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import apiClient from '@uniqcamp/shared/services/api.service';

interface SchoolRow {
  _id: string;
  name: string;
  uniqueCode: string;
  isActive: boolean;
  paymentStatus: string;
  onboardingStatus?: string;
  onboardingCompletedAt?: string;
  createdAt?: string;
}

interface SchoolsResponse {
  schools: SchoolRow[];
}

const Schools: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [isAdding, setIsAdding] = useState(false);
  const [name, setName] = useState('');
  const [uniqueCode, setUniqueCode] = useState('');
  const [emailId, setEmailId] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [city, setCity] = useState('');
  const [error, setError] = useState('');

  const { data, isLoading } = useQuery<SchoolsResponse>('schools', async () => {
    const response = await apiClient.get('/schools');
    return response.data as SchoolsResponse;
  });
  const schools = useMemo(() => {
    const list = data?.schools ?? [];
    return [...list].sort((a, b) => {
      const aCreated = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bCreated = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      if (aCreated !== bCreated) return bCreated - aCreated;
      return String(b._id).localeCompare(String(a._id));
    });
  }, [data?.schools]);

  interface CreateSchoolPayload {
    name: string;
    password: string;
    contact: { email: string; phone: string };
    uniqueCode: string;
    address?: { city: string };
  }

  const createSchoolMutation = useMutation(
    async (payload: CreateSchoolPayload) => {
      const response = await apiClient.post('/schools', payload);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('schools');
        setIsAdding(false);
        setName('');
        setUniqueCode('');
        setEmailId('');
        setPhone('');
        setPassword('');
        setCity('');
        setError('');
      },
      onError: (err: unknown) => {
        const e = err as { response?: { data?: { message?: string; error?: string } } };
        const msg =
          e.response?.data?.message ||
          e.response?.data?.error ||
          'Failed to create school';
        setError(msg);
      },
    }
  );

  const handleAddSchool = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');

    if (!name.trim() || !uniqueCode.trim()) {
      setError('Name and Unique Code are required');
      return;
    }

    createSchoolMutation.mutate({
      name: name.trim(),
      password: password.trim(),
      contact: {
        email: emailId.trim().toLowerCase(),
        phone: phone.trim(),
      },
      uniqueCode: uniqueCode.trim().toUpperCase(),
      address: city ? { city: city.trim() } : undefined,
    });
  };

  if (isLoading) return <div>Loading...</div>;

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h5" component="h1" fontWeight={600} sx={{ mb: 2 }}>
        Schools
      </Typography>

      <Paper sx={{ p: 2, mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" onClick={() => setIsAdding(true)}>
          Add School
        </Button>
      </Paper>

      {isAdding && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Onboard New School
          </Typography>
          <form onSubmit={handleAddSchool}>
            <Table sx={{ mb: 2 }}>
              <TableBody>
                <TableRow>
                  <TableCell width="20%">
                    <Typography variant="body2" fontWeight={600}>
                      Name*
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <input
                      style={{ width: '100%', padding: '8px' }}
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="e.g. Sunshine Public School"
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      Unique Code*
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <input
                      style={{ width: '100%', padding: '8px', textTransform: 'uppercase' }}
                      value={uniqueCode}
                      onChange={(e) => setUniqueCode(e.target.value)}
                      placeholder="e.g. SUN123 (cannot be changed later)"
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      Email Id*
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <input
                      style={{ width: '100%', padding: '8px', textTransform: 'uppercase' }}
                      value={emailId}
                      onChange={(e) => setEmailId(e.target.value)}
                      placeholder="Ener School Email Id"
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      Phone*
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <input
                      style={{ width: '100%', padding: '8px', textTransform: 'uppercase' }}
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="Enter School Phone Number"
                    />  
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      Password*
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <input
                      style={{ width: '100%', padding: '8px', textTransform: 'uppercase' }}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter School Password"
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      City
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <input
                      style={{ width: '100%', padding: '8px' }}
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      placeholder="Optional: City name"
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>

            {error && (
              <Typography color="error" sx={{ mb: 2 }}>
                {error}
              </Typography>
            )}

            <Button
              type="submit"
              variant="contained"
              sx={{ mr: 2 }}
              disabled={createSchoolMutation.isLoading}
            >
              {createSchoolMutation.isLoading ? 'Saving...' : 'Save'}
            </Button>
            <Button
              type="button"
              variant="text"
              onClick={() => {
                setIsAdding(false);
                setError('');
              }}
            >
              Cancel
            </Button>
          </form>
        </Paper>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Unique Code</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Payment Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {schools.map((school) => (
              <TableRow key={school._id}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    {school.name}
                    {school.onboardingStatus === 'ONBOARDED' &&
                      school.onboardingCompletedAt &&
                      new Date(school.onboardingCompletedAt).getTime() >= Date.now() - 30 * 24 * 60 * 60 * 1000 && (
                        <Chip label="New" color="primary" size="small" variant="outlined" />
                      )}
                  </Box>
                </TableCell>
                <TableCell>{school.uniqueCode}</TableCell>
                <TableCell>
                  <Chip
                    label={school.isActive ? 'Active' : 'Inactive'}
                    color={school.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={school.paymentStatus}
                    color={school.paymentStatus === 'ACTIVE' ? 'success' : 'warning'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => navigate(`/schools/${school._id}`)}
                  >
                    View Details
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default Schools;
